/**
 * Empty-state card for the no-report branch (DETAIL-PAGE-V2-PARITY §9 item 2):
 * shown when the AI report has no predictions AND the pipeline isn't processing
 * — the applicant hasn't completed their screening yet, generation failed, or
 * the submission is billing-held (TT-12). Replaces the prior behavior of
 * rendering the report sections with empty `-`/"Not Available" fields. The
 * step-data sections still render around it, so whatever the applicant HAS
 * already submitted stays visible.
 *
 * `variant='family'` (combined page, 2026-07-17) swaps the copy to the
 * HOUSEHOLD report — same card, same CTA shape, so the combined page's
 * failed/never-generated state can't drift from the individual one. The
 * `held` branch is individual-only (a billing hold is per-application).
 *
 * Variants (first match wins):
 * - `held` — TT-12 billing hold: the applicant DID submit, but the wallet was
 *   at/under floor so no paid services ran. Copy must never read as "applicant
 *   hasn't finished"; `onGenerate` posts to screening-resume-hold (which
 *   re-checks the wallet) rather than the regenerate dialog.
 * - `failed` — generation ran and errored; `onGenerate` opens the same
 *   (re)generate confirm dialog as the header kebab.
 * - default — waiting on the applicant; informational, no CTA.
 */
import { ClockFading, RefreshCw, TriangleAlert, Wallet } from 'lucide-react'
import { cn } from '#app/utils/misc'
import { PillButton } from './dialogs/dialog-bits'
import { DETAIL_CARD, IconSquare } from './primitives'

export function AwaitingReportSection({
	applicantName,
	variant = 'individual',
	failed = false,
	held = false,
	waitingOnApplicants = false,
	onGenerate,
	generatePending = false,
	generateError,
}: {
	/** Individual: whose report this is. Family: the household name — used
	 * only in the failed copy, which names what could not be generated. */
	applicantName: string
	/** 'family' = the combined page's household report. */
	variant?: 'individual' | 'family'
	/** `aiProcessingStatus === 'failed'` — generation ran and errored, vs. a
	 * report that simply hasn't been generated yet. */
	failed?: boolean
	/** TT-12 `application.billingHold` — submitted, report held on credits. */
	held?: boolean
	/** PARKED guarantor: deliberately waiting for the group's applicant FULL
	 * reports; generates automatically when they're ready (worker events +
	 * 15-min sweep). Informational — no CTA, nothing for the landlord to do. */
	waitingOnApplicants?: boolean
	/** Failed: opens the (re)generate confirm dialog. Held: posts the resume
	 * (no dialog — the endpoint re-checks the wallet and errors via toast). */
	onGenerate?: () => void
	/** Disables the CTA while the held-resume request is in flight. */
	generatePending?: boolean
	/** Held-resume failure (e.g. wallet still at/under floor) — shown inline. */
	generateError?: string
}) {
	const isFamily = variant === 'family'
	const title = waitingOnApplicants
		? 'Waiting for applicant reports'
		: held
			? 'Application submitted — report on hold'
			: failed
				? isFamily
					? 'Combined report could not be generated'
					: 'Screening report could not be generated'
				: // The combined page renders this card for FAILURES only (client
					// 2026-08-27) — its header already carries the waiting state — so
					// the family branch of the not-generated-yet copy is gone.
					'Screening report not generated yet'
	const body = waitingOnApplicants
		? `${applicantName} has submitted their application. The guarantor report is built on every applicant's full report, so it generates automatically once those are ready — no action needed. Anything already submitted is shown below.`
		: held
			? 'Your credit balance was too low to generate the report. Add credits, then generate it below.'
			: failed
				? isFamily
					? `We hit an error generating the combined report for this household. Nothing more is needed from the applicants — you can generate it again. Each member's individual report is unaffected and still available below.`
					: `We hit an error generating ${applicantName}'s report. Nothing more is needed from ${applicantName} — you can generate the report again. Anything already submitted is shown below.`
				: `${applicantName} hasn't completed their screening yet. Their report — scores, verification, and risk insights — will appear here once it's generated. Anything already submitted is shown below.`
	return (
		<section
			className={cn(
				DETAIL_CARD,
				'flex flex-col items-center gap-4 px-6 py-12 text-center sm:py-16',
			)}
		>
			<IconSquare tone={failed ? 'red' : 'amber'}>
				{held ? (
					<Wallet aria-hidden="true" />
				) : failed ? (
					<TriangleAlert aria-hidden="true" />
				) : (
					<ClockFading aria-hidden="true" />
				)}
			</IconSquare>
			<div className="max-w-md">
				<h2 className="text-[18px] font-semibold text-[#32302F]">{title}</h2>
				<p className="mt-2 text-[15px] leading-relaxed text-gray-500">{body}</p>
			</div>
			{(held || failed) && onGenerate ? (
				<PillButton
					tone="dark"
					onClick={onGenerate}
					disabled={generatePending}
					className="mt-1"
				>
					<RefreshCw className="size-4" />
					{generatePending ? 'Starting…' : 'Generate Report'}
				</PillButton>
			) : null}
			{generateError ? (
				<p className="text-sm text-[#DC2634]">{generateError}</p>
			) : null}
		</section>
	)
}
